import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalizeFirstLetterOfWords, TransactionStatus, TransactionType } from 'vopay';

/**
 * The transactions endpoint returns `TransactionStatus` and `TransactionType` as raw enum strings, and
 * both double as logic keys — the status drives the badge colour (`GetColorStatus`) and the refund
 * eligibility check, the type drives the details-page route and the "view" affordance. Per the
 * i18n-conventions "config-/API-derived labels that double as logic keys" rule the raw value is kept
 * verbatim everywhere it's matched on and only the *rendered* string is localized, here.
 *
 * Both maps are a `Record` over the enum (minus the deliberately-unmapped members below), so a missing
 * or misspelled member is a compile error and a newly-added enum member fails `tsc` until it is either
 * translated or added to the untranslated union — the type is what stops this list silently drifting
 * behind the backend enum.
 *
 * Both resolvers fall back to the raw value (title-cased, for statuses, as the list did before), so an
 * unmapped member renders as-is rather than blank. That fallback is also what keeps wordmarks intact:
 * the Interac types, VoPayInstant Withdraw, Visa Direct Push, Google Pay, PayPal, Venmo, Zelle, PayLink,
 * eLinx and Credit Card are deliberately unmapped (`Untranslated*` below) and must never be translated.
 *
 * Memoized on `t`, whose identity changes only when the language does — the resolvers run once per
 * rendered transaction row, so both maps are built once rather than on every render.
 */

// Empty status; no label to render.
type UntranslatedTransactionStatus = TransactionStatus.BLANK;

// Product/brand wordmarks that must render verbatim in every locale.
type UntranslatedTransactionType =
  | TransactionType.VOPAYINSTANT_WITHDRAWAL
  | TransactionType.INTERACT_MONEY_REQUEST
  | TransactionType.INTERACT_BULK_PAYOUT
  | TransactionType.VISA_DIRECT_PUSH
  | TransactionType.CREDIT_CARD
  | TransactionType.PAYLINK
  | TransactionType.GOOGLE_PAY
  | TransactionType.PAYPAL_WITHDRAWAL
  | TransactionType.VENMO_WITHDRAWAL
  | TransactionType.ZELLE_WITHDRAWAL
  | TransactionType.ELINX;

export default function useTransactionLabels() {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Transaction' });

  return useMemo(() => {
    // The status filter renders the same vocabulary from these keys — see TransactionsSearchForm.
    const statuses: Record<Exclude<TransactionStatus, UntranslatedTransactionStatus>, string> = {
      [TransactionStatus.REQUESTED]: t('Table.requested', { defaultValue: 'Requested' }),
      [TransactionStatus.PENDING]: t('Table.pending', { defaultValue: 'Pending' }),
      [TransactionStatus.IN_PROGRESS]: t('Table.inProgress', { defaultValue: 'In Progress' }),
      [TransactionStatus.SENT]: t('Table.sent', { defaultValue: 'Sent' }),
      [TransactionStatus.SUCCESSFUL]: t('Table.successful', { defaultValue: 'Successful' }),
      [TransactionStatus.COMPLETE]: t('Table.complete', { defaultValue: 'Complete' }),
      [TransactionStatus.SETTLED]: t('Table.settled', { defaultValue: 'Settled' }),
      [TransactionStatus.FAILED]: t('Table.failed', { defaultValue: 'Failed' }),
      [TransactionStatus.CANCELLED]: t('Table.cancelled', { defaultValue: 'Cancelled' }),
      [TransactionStatus.CANCELLATION_REQUESTED]: t('Table.cancellationRequested', {
        defaultValue: 'Cancellation Requested',
      }),
      [TransactionStatus.PENDING_APPROVAL]: t('Table.pendingApproval', { defaultValue: 'Pending Approval' }),
      [TransactionStatus.WAITING]: t('Table.waiting', { defaultValue: 'Waiting' }),
      [TransactionStatus.DECLINED]: t('Table.declined', { defaultValue: 'Declined' }),
      [TransactionStatus.RECEIVED]: t('Table.received', { defaultValue: 'Received' }),
      [TransactionStatus.PENDING_CONFIRMATION]: t('Table.pendingConfirmation', {
        defaultValue: 'Pending Confirmation',
      }),
      [TransactionStatus.BLOCKED_BY_FINANCIAL_INSTITUTION]: t('Table.blockedByFinancialInstitution', {
        defaultValue: 'Blocked by Financial Institution',
      }),
    };

    const types: Record<Exclude<TransactionType, UntranslatedTransactionType>, string> = {
      [TransactionType.ACH_FUNDING]: t('Type.achFunding', { defaultValue: 'ACH Funding' }),
      [TransactionType.ACH_WITHDRAWAL]: t('Type.achWithdrawal', { defaultValue: 'ACH Withdrawal' }),
      [TransactionType.CROSSBORDER_WITHDRAWAL]: t('Type.crossborderWithdrawal', {
        defaultValue: 'Crossborder Withdrawal',
      }),
      [TransactionType.EFT_FUNDING]: t('Type.eftFunding', { defaultValue: 'EFT Funding' }),
      [TransactionType.EFT_WITHDRAWAL]: t('Type.eftWithdrawal', { defaultValue: 'EFT Withdrawal' }),
      [TransactionType.FEE]: t('Type.fee', { defaultValue: 'Fee' }),
      [TransactionType.ACCOUNT_TRANSFER]: t('Type.accountTransfer', { defaultValue: 'Account Transfer' }),
      [TransactionType.REVERSAL]: t('Type.reversal', { defaultValue: 'Reversal' }),
      [TransactionType.SECURITY_DEPOSIT]: t('Type.securityDeposit', { defaultValue: 'Security Deposit' }),
      [TransactionType.OTHER_FUNDING]: t('Type.otherFunding', { defaultValue: 'Other Funding' }),
      [TransactionType.OTHER_WITHDRAWAL]: t('Type.otherWithdrawal', { defaultValue: 'Other Withdrawal' }),
      [TransactionType.CURRENCY_CONVERSION]: t('Type.currencyConversion', { defaultValue: 'Currency Conversion' }),
      [TransactionType.REVERSAL_INTERAC]: t('Type.reversalInterac', { defaultValue: 'Reversal Interac' }),
      [TransactionType.REVERSAL_MONEY_REQUEST]: t('Type.reversalMoneyRequest', {
        defaultValue: 'Reversal Money Request',
      }),
      [TransactionType.INTERNATIONAL_FUNDING]: t('Type.internationalFunding', {
        defaultValue: 'International Funding',
      }),
      [TransactionType.INTERNATIONAL_WITHDRAWAL]: t('Type.internationalWithdrawal', {
        defaultValue: 'International Withdrawal',
      }),
      [TransactionType.REFUND]: t('Type.refund', { defaultValue: 'Refund' }),
      [TransactionType.RTR_WITHDRAWAL]: t('Type.rtrWithdrawal', { defaultValue: 'RTR Withdrawal' }),
      [TransactionType.RTP_WITHDRAWAL]: t('Type.rtpWithdrawal', { defaultValue: 'RTP Withdrawal' }),
      [TransactionType.FEDWIRE_WITHDRAWAL]: t('Type.fedwireWithdrawal', { defaultValue: 'Fedwire Withdrawal' }),
      [TransactionType.FEDWIRE_FUNDING]: t('Type.fedwireFunding', { defaultValue: 'Fedwire Funding' }),
      [TransactionType.BANK_ACCOUNT]: t('Type.bankAccount', { defaultValue: 'Bank Account' }),
      [TransactionType.BILL_PAY]: t('Type.billPay', { defaultValue: 'Bill Pay' }),
      [TransactionType.AUTO_BALANCE_TRANSFER]: t('Type.autoBalanceTransfer', {
        defaultValue: 'Auto Balance Transfer',
      }),
      [TransactionType.VIRTUAL_ACCOUNT_DEPOSIT]: t('Type.virtualAccountDeposit', {
        defaultValue: 'Virtual Account Deposit',
      }),
      [TransactionType.VIRTUAL_ACCOUNT_FUNDING]: t('Type.virtualAccountFunding', {
        defaultValue: 'Virtual Account Funding',
      }),
      [TransactionType.VIRTUAL_ACCOUNT_WITHDRAW]: t('Type.virtualAccountWithdraw', {
        defaultValue: 'Virtual Account Withdraw',
      }),
      [TransactionType.VIRTUAL_ACCOUNT_WIRE]: t('Type.virtualAccountWire', { defaultValue: 'Virtual Account Wire' }),
      [TransactionType.INBOUND_E_TRANSFER]: t('Type.inboundETransfer', { defaultValue: 'Inbound e-Transfer' }),
    };

    // The exhaustive `Record`s above are the compile-time completeness check; lookups go through a
    // `Map` because the `Record`s exclude the untranslated members, so the full enum can't index them.
    const statusLabels = new Map<string, string>(Object.entries(statuses));
    const typeLabels = new Map<string, string>(Object.entries(types));

    return {
      resolveStatusLabel: (rawStatus: TransactionStatus): string =>
        statusLabels.get(rawStatus) ?? capitalizeFirstLetterOfWords(rawStatus),
      resolveTypeLabel: (rawType: TransactionType): string => typeLabels.get(rawType) ?? rawType,
    };
  }, [t]);
}
